/**
 * Reasoning Cache (IndexedDB)
 *
 * Caches reasoning results keyed by plan token and max sequence number.
 * Since the log is append-only, we can use maxSeq as a cache invalidation key.
 *
 * Uses IndexedDB for:
 * - Larger storage limits (hundreds of MB vs 5-10MB for localStorage)
 * - Async/non-blocking reads and writes
 * - Direct object storage (no JSON serialization overhead)
 */

import type { Conclusion, MetaEntry } from "./spindle";

interface CachedReasoning {
  token: string; // primary key
  maxSeq: number;
  conclusions: Conclusion[];
  meta: MetaEntry[];
  cachedAt: number;
}

const DB_NAME = "hence-reasoning-cache";
const DB_VERSION = 1;
const STORE_NAME = "reasoning";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Get or initialize the IndexedDB database
 */
function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "token" });
      }
    };
  });

  return dbPromise;
}

/**
 * Get cached reasoning results for a plan
 */
export async function getCachedReasoning(
  token: string,
  currentMaxSeq: number
): Promise<{ conclusions: Conclusion[]; meta: MetaEntry[] } | null> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    return new Promise((resolve) => {
      const request = store.get(token);

      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const data: CachedReasoning | undefined = request.result;

        if (!data) {
          resolve(null);
          return;
        }

        // Cache hit only if maxSeq matches exactly (append-only log)
        if (data.maxSeq !== currentMaxSeq) {
          resolve(null);
          return;
        }

        // Check TTL
        if (Date.now() - data.cachedAt > CACHE_TTL_MS) {
          // Expired - clean up async
          clearCachedReasoning(token).catch(() => {});
          resolve(null);
          return;
        }

        resolve({
          conclusions: data.conclusions,
          meta: data.meta,
        });
      };
    });
  } catch {
    return null;
  }
}

/**
 * Store reasoning results in cache
 */
export async function setCachedReasoning(
  token: string,
  maxSeq: number,
  conclusions: Conclusion[],
  meta: MetaEntry[]
): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    const data: CachedReasoning = {
      token,
      maxSeq,
      conclusions,
      meta,
      cachedAt: Date.now(),
    };

    store.put(data);
  } catch {
    // IndexedDB may be unavailable - ignore
  }
}

/**
 * Clear cached reasoning for a plan
 */
export async function clearCachedReasoning(token: string): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(token);
  } catch {
    // ignore
  }
}
